import { formatNumberWithCommas } from '@copmer/calculator-integration/shared';
import { Fragment, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { CommodityOption } from '@/actions/products/types';
import { Button } from '@/components/ui/button';
import { PortSelectOption } from '@/lib/port-options';
import { convertCbftToHectoliterFormatted } from '@/lib/units';
import { cn } from '@/lib/utils';

import { useCalculatorContext } from './context';
import { CopyButton } from './CopyButton';
import { CurrencyDropdown } from './CurrencySelectDropdown';
import Icon from './Icon';
import { MultiModeInput } from './schema';
import { SimpleCalculatorFormData, resolveStowageFactor } from './types';

const VoyageList = ({ ports }: { ports: string[] }) => {
    return (
        <div className="flex flex-wrap flex-row gap-2 items-center gap-y-0">
            {ports.map((port, index) => {
                return (
                    <Fragment key={index}>
                        <div className="text-sm text-white/50 max-md:text-white/60">{port}</div>
                        {index !== ports.length - 1 ? (
                            <Icon icon="ArrowRight" className="h-4 w-4 text-white/75 max-md:text-white/60" />
                        ) : null}
                    </Fragment>
                );
            })}
        </div>
    );
};

const isMultiModeForm = (value: any, type: 'single' | 'multi'): value is UseFormReturn<MultiModeInput> => {
    return type === 'multi';
};

interface CalculationLookupSummaryProps {
    form: UseFormReturn<SimpleCalculatorFormData> | UseFormReturn<MultiModeInput>;

    commodities: CommodityOption[];
    ports: PortSelectOption[];

    onStartEditing: () => void;

    isMobile?: boolean;

    setSortModalOpen?: (isOpen: boolean) => void;
    setFilterModalOpen?: (isOpen: boolean) => void;

    formType?: 'single' | 'multi';
}

const formatNumber = (value: number): string => {
    const res = formatNumberWithCommas(value, false);

    if (res.endsWith('.00')) {
        return res.slice(0, -3);
    }
    if (res.endsWith('.0')) {
        return res.slice(0, -2);
    }
    return res;
};

export default function CalculationLookupSummary({
    form,
    commodities,
    ports,
    onStartEditing,
    isMobile,
    setSortModalOpen,
    setFilterModalOpen,
    formType = 'single',
}: CalculationLookupSummaryProps) {
    const ctx = useCalculatorContext();

    let commodityNode = null;

    if (isMultiModeForm(form, formType)) {
        commodityNode = 'Multiple commodities';
    } else {
        const fields = form.watch(['commodity', 'stowageFactor', 'stowageFactorUnit']);

        const commodity = fields[0] as string;
        let stowageFactor = fields[1] as number | undefined;

        let commodityData = commodities.find((c) => c.value === commodity);
        let commodityName = commodityData?.displayName;

        if (!commodityName) {
            // see if we can find a commodity by frcId
            commodityData = commodities.find((c) => c.frcId === commodity);
            commodityName = commodityData?.displayName;
        }

        const stowageFactorUnit = fields[2] as 'cbft' | 'kghl';

        // same resolution used when building the submit payload, so the summary never shows a
        //  value that differs from what actually gets sent to FRC
        stowageFactor = resolveStowageFactor(stowageFactor, stowageFactorUnit, commodityData?.stowageFactorCBFT);

        if (!commodityName) {
            commodityName = commodity;
        }

        let stowageFactorLabel = '';

        if (typeof stowageFactor !== 'undefined' && stowageFactor !== null) {
            stowageFactorLabel = `(${formatNumber(
                stowageFactor
            )} cbft/mt, ${convertCbftToHectoliterFormatted(stowageFactor)} kg/hl)`;
        }

        commodityNode = (
            <>
                {commodityName}
                {stowageFactorLabel ? <> {stowageFactorLabel}</> : ''}
            </>
        );
    }

    const portCodes = isMultiModeForm(form, formType)
        ? (form.watch('ports').map((port) => port.port ?? '') ?? [])
        : [form.watch('startPort.port'), form.watch('endPort.port')];

    const portList = useMemo(() => {
        return portCodes.map((portCode) => {
            return ports.find((p) => p.unlocode === portCode)?.displayName ?? portCode;
        });
    }, [portCodes]);

    return (
        <div
            className={cn('py-4 flex flex-row', {
                'px-2': !isMobile,
                'px-4': isMobile,
            })}
        >
            <div role="button" title="Edit" onClick={onStartEditing}>
                <div className="text-white text-base font-light max-md:text-sm max-md:text-white/80">
                    {commodityNode}
                </div>
                <VoyageList ports={portList} />
            </div>

            {isMobile ? <div className="grow" /> : null}

            <div
                className={cn('flex items-center justify-center', {
                    'pl-11 pr-2': !isMobile,
                    'gap-1': isMobile,
                })}
            >
                {isMobile && !ctx?.isCFROptimizer ? (
                    <Button variant="icon" size="icon" onClick={() => setSortModalOpen?.(true)}>
                        <Icon icon="ArrowsSort" className="h-6 w-6" />
                    </Button>
                ) : null}
                {isMobile && !ctx?.isCFROptimizer ? (
                    <Button variant="icon" size="icon" onClick={() => setFilterModalOpen?.(true)}>
                        <Icon icon="Filter" className="h-6 w-6" />
                    </Button>
                ) : null}

                <Button variant="icon" size={isMobile ? 'icon' : 'iconText'} onClick={onStartEditing}>
                    <Icon icon="Edit" className="h-6 w-6" />
                    {isMobile ? null : <span className="ml-2">Edit</span>}
                </Button>

                {isMobile && !ctx?.isCFROptimizer ? <CopyButton /> : null}

                {isMobile ? <CurrencyDropdown noLabel isMobileHeader /> : null}
            </div>
        </div>
    );
}
